/**
 * Dentli booking widgets.
 *
 * Dentli's loader (obloader.js) is fetched lazily — only once a widget is
 * actually needed — and defines window.registerComponents(), which scans the
 * page for .dentli_booking_container elements and renders them.
 *
 * - loadDentliLoader() / mountDentliWidget(): used by emergency-lightbox.js
 *   when a clinic is picked.
 * - [data-dentli-widget="<widget id>"] (+ optional [data-dentli-treatment]):
 *   inline widgets in page markup (single-clinic.php's booking card), mounted
 *   once they scroll near the viewport.
 */

const DENTLI_LOADER_SRC = 'https://eu.core.dentli.io/vbslb/bcfjx/obloader.js';
let dentliLoaderPromise = null;

export function loadDentliLoader() {
	if ( typeof window.registerComponents === 'function' ) {
		return Promise.resolve();
	}
	if ( ! dentliLoaderPromise ) {
		dentliLoaderPromise = new Promise( ( resolve ) => {
			const script = document.createElement( 'script' );
			script.src = DENTLI_LOADER_SRC;
			script.async = true;
			script.onload = () => resolve();
			script.onerror = () => resolve(); // fail soft — the widget just won't render
			document.head.appendChild( script );
		} );
	}
	return dentliLoaderPromise;
}

function escapeAttr( value ) {
	return String( value ).replace( /[&"<>]/g, ( c ) => ( { '&': '&amp;', '"': '&quot;', '<': '&lt;', '>': '&gt;' }[ c ] ) );
}

export function mountDentliWidget( mount, widgetId, treatmentId ) {
	const treatmentAttr = treatmentId
		? ` data-dentli_ob_treatment_id="${ escapeAttr( treatmentId ) }"`
		: '';

	mount.innerHTML =
		'<div class="dentli_booking_container" data-dentli_ob_mode="quick-mini"' +
		` data-dentli_ob_id="${ escapeAttr( widgetId ) }"${ treatmentAttr }` +
		' data-dentli_ob_show_text="false"></div>';

	loadDentliLoader().then( () => {
		if ( typeof window.registerComponents === 'function' ) {
			window.registerComponents();
		}
	} );
}

function initInlineWidgets() {
	const mounts = document.querySelectorAll( '[data-dentli-widget]' );
	if ( ! mounts.length ) {
		return;
	}

	const mount = ( el ) => mountDentliWidget( el, el.dataset.dentliWidget, el.dataset.dentliTreatment );

	if ( ! ( 'IntersectionObserver' in window ) ) {
		mounts.forEach( mount );
		return;
	}

	const observer = new IntersectionObserver( ( entries ) => {
		entries.forEach( ( entry ) => {
			if ( entry.isIntersecting ) {
				observer.unobserve( entry.target );
				mount( entry.target );
			}
		} );
	}, { rootMargin: '400px 0px' } );

	mounts.forEach( ( el ) => observer.observe( el ) );
}

initInlineWidgets();
